import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

const TOKEN_TIMEOUT_SECONDS = 10;
const REQUEST_TIMEOUT_MS = 15_000;

let authRedirectInProgress = false;

const isRecoverableAuthError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const authError = error as Error & { error?: string; code?: string };
  const details = `${authError.name} ${authError.error || ""} ${
    authError.code || ""
  } ${authError.message}`.toLowerCase();
  return [
    "login_required",
    "consent_required",
    "missing refresh token",
    "missing_refresh_token",
    "invalid refresh token",
    "invalid_grant",
    "timeouterror",
    "timeout when executing",
  ].some((value) => details.includes(value));
};

const getUrl = (endpoint?: string) => {
  const normalizedEndpoint = (endpoint || "api").replace(/^\/+|\/+$/g, "");
  return `/${normalizedEndpoint}`;
};

export const API_URL = getUrl(process.env.REACT_APP_API_ENDPOINT);

export const useApi = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const callApi = useCallback(
    async (uri: string, options: RequestInit = {}, scope: string) => {
      let token: string;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: `offline_access ${scope}`,
          },
          timeoutInSeconds: TOKEN_TIMEOUT_SECONDS,
        });
      } catch (error) {
        if (isRecoverableAuthError(error) && !authRedirectInProgress) {
          authRedirectInProgress = true;
          void loginWithRedirect({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: "openid profile email offline_access",
              prompt: "login",
            },
            appState: {
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          }).catch(() => {
            authRedirectInProgress = false;
          });
        }
        throw error;
      }
      const headers = new Headers(options.headers || {});
      headers.append("Authorization", `Bearer ${token}`);

      const controller = new AbortController();
      const abortRequest = () => controller.abort();
      if (options.signal?.aborted) controller.abort();
      else options.signal?.addEventListener("abort", abortRequest, { once: true });
      const timeout = window.setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT_MS
      );

      try {
        return await fetch(`${API_URL}/${uri}`, {
          ...options,
          headers,
          signal: controller.signal,
        });
      } catch (error) {
        if (controller.signal.aborted && !options.signal?.aborted) {
          throw new Error(
            "The server took too long to respond. Please try again."
          );
        }
        throw error;
      } finally {
        window.clearTimeout(timeout);
        options.signal?.removeEventListener("abort", abortRequest);
      }
    },
    [getAccessTokenSilently, loginWithRedirect]
  );

  return callApi;
};
